import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useToast } from '../context/ToastContext';
import { formatPrice } from '../utils/coursePricing';
import { seasonTermLabel } from '../utils/seasonLabel';
import Button from './ui/Button';
import ConfirmModal from './ui/ConfirmModal';
import PaymentQRModal from './PaymentQRModal';
import {
  DivisionScheme,
  DivisionSegmentKind,
  ResolvedSegment,
  resolveDivisionSegments,
} from '../schemaForm/fields/divisionScheme';
import '../App.css';
import './MyMembershipsPanel.css';
import { translateError } from '../utils/errorMessages';

// =============================================================================
// TYPES — mirror the GET /api/my/memberships contract
// =============================================================================

type MembershipType = 'season' | 'calendar' | 'time' | 'entry';
type PaymentState = 'paid' | 'locked' | 'pending' | 'none';

interface PriceOption {
  interval?: string;
  durationDays?: number | null;
  amount: number;
  entryCount?: number | null;
}

interface OfferOption {
  _id: number;
  name: string;
  type: MembershipType;
  entryCount?: number | null;
  priceOptions: PriceOption[];
  // Season-division: the split definition + the season this offer resolves
  // against, so the stretch picker can render concrete dates + prices.
  seasonId?: number;
  divisionScheme?: DivisionScheme | null;
}

interface NeedsChoiceEntry {
  course: {
    _id: number;
    name: string;
    /** Active-season term bounds — course-scoped options are labelled by these. */
    termStart?: string | null;
    termEnd?: string | null;
    /**
     * Active-season capacity + enrolment (display only). NOT used to gate the
     * choose action: needsChoice members are already enrolled and hold a seat, so
     * choosing is a payment, not a new enrollment, and a full season must not block it.
     */
    capacity?: number | null;
    enrolledCount?: number;
  };
  offers: OfferOption[];
}

interface AssignmentIntent {
  amount: number;
  paidAmount: number;
  status: string;
  variableSymbol: number;
}

interface AssignmentEntry {
  membershipId: number;
  membershipName: string;
  type: MembershipType;
  status: string;
  interval: string | null;
  durationDays: number | null;
  periodStart: string | null;
  periodEnd: string | null;
  totalEntries: number | null;
  usedEntries: number;
  intent: AssignmentIntent | null;
  paymentState: PaymentState;
}

interface MemberMembershipGroup {
  member: { _id: number; name: string };
  assignments: AssignmentEntry[];
  needsChoice: NeedsChoiceEntry[];
}

interface MyMembershipsPanelProps {
  /** When provided, only this member's group is shown. Undefined = all members. */
  memberId?: number;
  /** Fired after a successful choose/cancel so callers can refresh badges. */
  onChanged?: () => void;
}

// =============================================================================
// HELPERS
// =============================================================================

/** Stable key for a price option radio within an offer. */
const optionKey = (offer: OfferOption, opt: PriceOption, index: number): string =>
  `${offer._id}:${opt.interval ?? opt.durationDays ?? opt.entryCount ?? index}:${index}`;

const isLocked = (err: any): boolean =>
  err?.response?.data?.code === 'MEMBERSHIP_LOCKED';

// =============================================================================
// COMPONENT
// =============================================================================

const MyMembershipsPanel: React.FC<MyMembershipsPanelProps> = ({ memberId, onChanged }) => {
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();
  const locale = i18n.language;

  const [groups, setGroups] = useState<MemberMembershipGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Selected price option per course needs-choice card, keyed by memberId:courseId.
  const [selected, setSelected] = useState<Record<string, string>>({});

  // Cancel-choice confirmation target.
  const [cancelTarget, setCancelTarget] = useState<{ memberId: number; membershipId: number } | null>(null);

  // Payment-QR modal target (assignment with an open amount).
  const [qrTarget, setQrTarget] = useState<{
    member: { _id: number; name: string };
    assignment: AssignmentEntry;
  } | null>(null);

  const fetchMemberships = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.my.memberships();
      setGroups(data as MemberMembershipGroup[]);
      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMemberships();
  }, [fetchMemberships]);

  const visibleGroups = useMemo(
    () => (memberId != null ? groups.filter((g) => g.member._id === memberId) : groups),
    [groups, memberId],
  );

  const formatDate = useCallback(
    (value: string | null): string =>
      value ? new Date(value).toLocaleDateString(locale || undefined) : '',
    [locale],
  );

  /** Localized qualifier + price for a single price option row. */
  const optionParts = useCallback(
    (
      offer: OfferOption,
      opt: PriceOption,
      course: NeedsChoiceEntry['course'],
    ): { qualifier: string; price: string } => {
      const price = formatPrice(opt.amount, locale) ?? '';
      let qualifier = '';
      const hasTerm = !!(course.termStart || course.termEnd);
      if (offer.type === 'entry' && opt.entryCount != null) {
        // Entry packs are count-based coverage, not a season span.
        qualifier = t('courses.entries', { count: opt.entryCount });
      } else if (hasTerm) {
        // Course-scoped memberships derive coverage from the active-season term.
        qualifier = seasonTermLabel(course.termStart, course.termEnd, t, locale);
      } else if (offer.type === 'calendar' && opt.interval) {
        qualifier = t(`courses.interval.${opt.interval}`, opt.interval);
      } else if (offer.type === 'time' && opt.durationDays != null) {
        qualifier = t('courses.durationDays', { count: opt.durationDays });
      }
      return { qualifier, price };
    },
    [locale, t],
  );

  const handleChoose = useCallback(
    async (mId: number, course: NeedsChoiceEntry) => {
      const key = `${mId}:${course.course._id}`;
      const chosenKey = selected[key];
      if (!chosenKey) return;

      setSubmitting(true);
      try {
        if (chosenKey.startsWith('season:')) {
          // Season-division stretch pick: key = season:<offerId>:<scheme>:<index|f>.
          const [, offerIdRaw, scheme, idxRaw] = chosenKey.split(':');
          const offer = course.offers.find((o) => o._id === Number(offerIdRaw));
          if (!offer) return;
          await api.my.chooseMembership(mId, {
            membershipId: offer._id,
            seasonId: offer.seasonId,
            segmentScheme: scheme,
            segmentIndex: scheme === 'full' ? null : Number(idxRaw),
          });
        } else {
          // Resolve the chosen offer + option from the encoded key.
          let chosenOffer: OfferOption | undefined;
          let chosenOpt: PriceOption | undefined;
          course.offers.forEach((offer) => {
            offer.priceOptions.forEach((opt, idx) => {
              if (optionKey(offer, opt, idx) === chosenKey) {
                chosenOffer = offer;
                chosenOpt = opt;
              }
            });
          });
          if (!chosenOffer || !chosenOpt) return;
          await api.my.chooseMembership(mId, {
            membershipId: chosenOffer._id,
            interval: chosenOpt.interval,
            durationDays: chosenOpt.durationDays ?? undefined,
          });
        }
        addToast(t('memberships.chosen'), 'success');
        setSelected((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        await fetchMemberships();
        onChanged?.();
      } catch (err: any) {
        const message = isLocked(err)
          ? t('memberships.lockedNote')
          : translateError(err, t);
        addToast(t('common.error') + ': ' + message, 'error');
      } finally {
        setSubmitting(false);
      }
    },
    [selected, addToast, t, fetchMemberships, onChanged],
  );

  const handleCancelConfirm = useCallback(async () => {
    if (!cancelTarget) return;
    setSubmitting(true);
    try {
      await api.my.cancelMembership(cancelTarget.memberId, cancelTarget.membershipId);
      addToast(t('memberships.cancelled'), 'success');
      setCancelTarget(null);
      await fetchMemberships();
      onChanged?.();
    } catch (err: any) {
      const message = isLocked(err)
        ? t('memberships.lockedNote')
        : translateError(err, t);
      addToast(t('common.error') + ': ' + message, 'error');
      setCancelTarget(null);
    } finally {
      setSubmitting(false);
    }
  }, [cancelTarget, addToast, t, fetchMemberships, onChanged]);

  // ---------------------------------------------------------------------------
  // RENDER HELPERS
  // ---------------------------------------------------------------------------

  const renderPaymentBadge = (a: AssignmentEntry): React.ReactNode => {
    if (a.paymentState === 'paid') {
      return <span className="badge badge-success">✓ {t('memberships.paid')}</span>;
    }
    if (a.paymentState === 'pending') {
      const amount = a.intent?.amount ?? 0;
      const remaining = amount - (a.intent?.paidAmount ?? 0);
      return (
        <span className="badge badge-warning mm-badge-stack">
          <span>{t('memberships.awaitingPayment', { amount, vs: a.intent?.variableSymbol ?? '' })}</span>
          {remaining > 0 && remaining !== amount && (
            <span>{t('memberships.remaining', { amount: remaining })}</span>
          )}
        </span>
      );
    }
    if (a.paymentState === 'locked') {
      return (
        <span className="badge badge-warning" title={t('memberships.lockedNote')}>
          🔒 {t('memberships.lockedNote')}
        </span>
      );
    }
    return <span className="badge badge-muted">—</span>;
  };

  const renderAssignment = (member: { _id: number; name: string }, a: AssignmentEntry): React.ReactNode => {
    const mId = member._id;
    const unlocked = a.paymentState === 'pending' || a.paymentState === 'none';
    const remaining = a.intent ? a.intent.amount - a.intent.paidAmount : 0;
    const payable =
      (a.paymentState === 'pending' || a.paymentState === 'locked') && remaining > 0;
    const period =
      a.periodStart || a.periodEnd
        ? `${formatDate(a.periodStart)} – ${formatDate(a.periodEnd)}`
        : null;

    return (
      <div key={a.membershipId} className="mm-assignment">
        <div className="mm-assignment__row">
          <div>
            <strong className="mm-assignment__name">{a.membershipName}</strong>
            <div className="mm-assignment__meta">
              <span>{t(`memberships.type${a.type.charAt(0).toUpperCase() + a.type.slice(1)}`, a.type)}</span>
              {period && <> · {period}</>}
              {a.type === 'entry' && a.totalEntries != null && (
                <> · {a.usedEntries}/{a.totalEntries} {t('courses.entries', { count: a.totalEntries })}</>
              )}
            </div>
          </div>
          <div className="mm-assignment__side">
            {renderPaymentBadge(a)}
            <div className="mm-assignment__actions">
              {payable && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setQrTarget({ member, assignment: a })}
                  title={t('payments.generateQR')}
                >
                  QR
                </Button>
              )}
              {unlocked && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCancelTarget({ memberId: mId, membershipId: a.membershipId })}
                >
                  {t('memberships.cancelChoice')}
                </Button>
              )}
            </div>
            {!unlocked && a.paymentState === 'locked' && (
              <span className="mm-locked-note">{t('memberships.lockedNote')}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // --- season-division stretch picker --------------------------------------
  const seasonKey = (offerId: number, seg: ResolvedSegment): string =>
    `season:${offerId}:${seg.scheme}:${seg.index ?? 'f'}`;

  const unitFor = useCallback(
    (kind: DivisionSegmentKind): string => {
      switch (kind) {
        case 'halves':
          return t('memberships.unitHalf');
        case 'thirds':
          return t('memberships.unitThird');
        case 'quarters':
          return t('memberships.unitQuarter');
        default:
          return t('memberships.unitPart');
      }
    },
    [t],
  );
  const schemeTitle = useCallback(
    (kind: DivisionSegmentKind): string => {
      switch (kind) {
        case 'halves':
          return t('memberships.schemeByHalves');
        case 'thirds':
          return t('memberships.schemeByThirds');
        case 'quarters':
          return t('memberships.schemeByQuarters');
        default:
          return t('memberships.schemeCustom');
      }
    },
    [t],
  );
  const segName = useCallback(
    (seg: ResolvedSegment): string =>
      seg.scheme === 'full'
        ? t('memberships.wholeSeason')
        : t('memberships.segmentLabel', { index: (seg.index ?? 0) + 1, unit: unitFor(seg.scheme) }),
    [t, unitFor],
  );

  const renderCoverage = (
    seg: ResolvedSegment,
    span: number | null,
  ): React.ReactNode => {
    const total = span ?? seg.days;
    const leftPct = (seg.startOffset / total) * 100;
    const widthPct = (seg.days / total) * 100;
    const isFull = seg.scheme === 'full';
    return (
      <div className="mm-coverage">
        <div className="mm-coverage__track">
          {seg.startOffset > 0 && (
            <div className="mm-coverage__empty" style={{ left: 0, width: `${leftPct}%` }} />
          )}
          <div className="mm-coverage__fill" style={{ left: `${leftPct}%`, width: `${widthPct}%` }}>
            {t('memberships.covered')}
          </div>
          {seg.endOffset + 1 < total && (
            <div
              className="mm-coverage__empty"
              style={{ left: `${((seg.endOffset + 1) / total) * 100}%`, width: `${((total - seg.endOffset - 1) / total) * 100}%` }}
            />
          )}
        </div>
        <div className="mm-coverage__stats">
          <span>
            <strong>{formatPrice(seg.price, locale)}</strong>
          </span>
          {seg.startDate && seg.endDate && (
            <span>{formatDate(seg.startDate.toISOString())} – {formatDate(seg.endDate.toISOString())}</span>
          )}
          <span>{t('memberships.coversDays', { count: seg.days })}</span>
          {!isFull && <span className="mm-coverage__note">{t('memberships.uncoveredNote')}</span>}
        </div>
      </div>
    );
  };

  const renderSeasonOffer = (
    mId: number,
    nc: NeedsChoiceEntry,
    offer: OfferOption,
  ): React.ReactNode => {
    const key = `${mId}:${nc.course._id}`;
    const scheme = offer.divisionScheme!;
    const resolved = resolveDivisionSegments(scheme, nc.course.termStart, nc.course.termEnd);

    const groups: Array<{ title: string; segments: ResolvedSegment[] }> = [
      { title: t('memberships.wholeYear'), segments: [resolved.full] },
      ...scheme.schemes.map((s) => ({
        title: schemeTitle(s.kind),
        segments: resolved.schemes[s.kind] ?? [],
      })),
    ];

    // Find the currently-selected segment for this offer (for the coverage viz).
    let selectedSeg: ResolvedSegment | null = null;
    for (const g of groups) {
      for (const seg of g.segments) {
        if (selected[key] === seasonKey(offer._id, seg)) selectedSeg = seg;
      }
    }

    return (
      <div key={offer._id} className="mm-offer mm-offer--season">
        <div className="mm-offer__name">{offer.name}</div>
        {groups.map((g) => (
          <div key={g.title} className="mm-stretch-group">
            <div className="mm-stretch-group__title">{g.title}</div>
            <div className="mm-stretch-grid">
              {g.segments.map((seg) => {
                const k = seasonKey(offer._id, seg);
                const isSelected = selected[key] === k;
                return (
                  <button
                    type="button"
                    key={k}
                    className={`mm-plan${isSelected ? ' is-selected' : ''}`}
                    aria-pressed={isSelected}
                    onClick={() => setSelected((prev) => ({ ...prev, [key]: k }))}
                  >
                    <span className="mm-plan__name">{segName(seg)}</span>
                    {seg.startDate && seg.endDate && (
                      <span className="mm-plan__dates">
                        {formatDate(seg.startDate.toISOString())} – {formatDate(seg.endDate.toISOString())}
                      </span>
                    )}
                    <span className="mm-plan__price">{formatPrice(seg.price, locale)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {selectedSeg && renderCoverage(selectedSeg, resolved.span)}
      </div>
    );
  };

  const renderNeedsChoice = (mId: number, nc: NeedsChoiceEntry): React.ReactNode => {
    const key = `${mId}:${nc.course._id}`;
    // Course-scoped options all cover the SAME active-season term, so surface it
    // once, prominently, so the member knows exactly which season the payment buys.
    const hasTerm = !!(nc.course.termStart || nc.course.termEnd);
    // NOTE: no Full-gating here. needsChoice entries come from EXISTING enrollments,
    // so the member already holds a capacity seat (already in the roster count).
    // Choosing here is a PAYMENT action for that held seat, not a new enrollment,
    // so a full season must not block it.
    return (
      <div key={nc.course._id} className="mm-choice">
        <p className="mm-choice__title">
          {t('memberships.chooseFor', { course: nc.course.name })}
        </p>
        {hasTerm && (
          <p className="mm-choice__season">
            {t('memberships.paymentCoversSeason')}{' '}
            <strong className="mm-choice__season-term">
              {seasonTermLabel(nc.course.termStart, nc.course.termEnd, t, locale)}
            </strong>
          </p>
        )}
        <div className="mm-choice__offers">
          {nc.offers.map((offer) =>
            offer.type === 'season' && offer.divisionScheme ? (
              renderSeasonOffer(mId, nc, offer)
            ) : (
            <div key={offer._id} className="mm-offer">
              <div className="mm-offer__name">{offer.name}</div>
              <div className="mm-offer__options">
                {offer.priceOptions.map((opt, idx) => {
                  const k = optionKey(offer, opt, idx);
                  const { qualifier, price } = optionParts(offer, opt, nc.course);
                  const isSelected = selected[key] === k;
                  return (
                    <label key={k} className={`mm-option${isSelected ? ' is-selected' : ''}`}>
                      <input
                        type="radio"
                        name={key}
                        checked={isSelected}
                        onChange={() => setSelected((prev) => ({ ...prev, [key]: k }))}
                      />
                      <span className="mm-option__label">{qualifier || price}</span>
                      {qualifier && <span className="mm-option__price">{price}</span>}
                      <span className="mm-option__check" aria-hidden="true">✓</span>
                    </label>
                  );
                })}
              </div>
            </div>
            ),
          )}
        </div>
        <div className="mm-choice__action">
          <Button
            onClick={() => handleChoose(mId, nc)}
            disabled={!selected[key] || submitting}
            loading={submitting}
          >
            {t('memberships.choose')}
          </Button>
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  if (loading && groups.length === 0) return <p>{t('common.loading')}</p>;
  if (error) return <p className="error-message">{t('common.error')}: {error}</p>;

  if (visibleGroups.length === 0) {
    return <p className="mm-empty">{t('memberships.noMemberships')}</p>;
  }

  return (
    <div>
      {visibleGroups.map((group) => {
        const hasContent = group.assignments.length > 0 || group.needsChoice.length > 0;
        return (
          <div key={group.member._id} className="mm-group">
            {memberId == null && <h3 className="mm-group__member">{group.member.name}</h3>}
            {!hasContent && <p className="mm-empty">{t('memberships.noMemberships')}</p>}
            {group.needsChoice.map((nc) => renderNeedsChoice(group.member._id, nc))}
            {group.assignments.map((a) => renderAssignment(group.member, a))}
          </div>
        );
      })}

      <ConfirmModal
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancelConfirm}
        title={t('memberships.cancelChoice')}
        message={t('memberships.cancelConfirm')}
        confirmText={t('memberships.cancelChoice')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={submitting}
      />

      {qrTarget && (
        <PaymentQRModal
          isOpen={!!qrTarget}
          onClose={() => setQrTarget(null)}
          type="membership"
          entityId={qrTarget.assignment.membershipId}
          entityName={qrTarget.assignment.membershipName}
          memberId={qrTarget.member._id}
          memberName={qrTarget.member.name}
          amount={qrTarget.assignment.intent
            ? qrTarget.assignment.intent.amount - qrTarget.assignment.intent.paidAmount
            : 0}
          durationDays={qrTarget.assignment.durationDays ?? undefined}
        />
      )}
    </div>
  );
};

export default MyMembershipsPanel;
